import { useEffect, useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import api from "../lib/api";
import { toast } from "sonner";
import { Users, CheckCircle2, Clock, ShieldCheck, Sparkles, Mail, Phone, Send } from "lucide-react";

/**
 * MyIntroducteesCard — shows the authenticated user every person they've
 * introduced into the EchoSense Capital Club, with each introductee's
 * current onboarding stage.
 *
 * Stages (in order):
 *   registered → kyc_in_review → kyc_approved → accredited_in_review →
 *   accredited_approved → accredited_kyc_approved (final milestone)
 */
const STAGE_LABELS = {
  registered:               { label: "Registered",        cls: "bg-slate-50 text-slate-600 border-slate-200",  icon: Clock },
  kyc_in_review:            { label: "KYC In Review",     cls: "bg-amber-50 text-amber-700 border-amber-200",  icon: Clock },
  kyc_approved:             { label: "KYC Approved",      cls: "bg-blue-50 text-blue-700 border-blue-200",     icon: CheckCircle2 },
  accredited_in_review:     { label: "Accredited Review", cls: "bg-amber-50 text-amber-700 border-amber-200",  icon: Clock },
  accredited_approved:      { label: "Accredited",        cls: "bg-blue-50 text-blue-700 border-blue-200",     icon: CheckCircle2 },
  accredited_kyc_approved:  { label: "Fully Approved",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Sparkles },
};

const STAGE_PROGRESS = {
  registered:               20,
  kyc_in_review:            40,
  kyc_approved:             55,
  accredited_in_review:     70,
  accredited_approved:      85,
  accredited_kyc_approved:  100,
};

const MyIntroducteesCard = () => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudgingId, setNudgingId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/me/introductees");
      setRows(r.data.introductees || []);
    } catch (_) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const nudge = async (introductee) => {
    setNudgingId(introductee.id);
    try {
      await api.post(`/me/introductees/${introductee.id}/nudge`);
      toast.success(`Nudge sent to ${introductee.full_name || introductee.email}`);
      load(); // refresh last_nudge_at on the row
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send nudge");
    } finally {
      setNudgingId(null);
    }
  };

  // Is this introductee currently within the 7-day cooldown? Returns next eligible date or null.
  const nudgeCooldownUntil = (lastNudgeAt) => {
    if (!lastNudgeAt) return null;
    const last = new Date(lastNudgeAt);
    if (Number.isNaN(last.getTime())) return null;
    const next = new Date(last.getTime() + 7 * 24 * 60 * 60 * 1000);
    return next > new Date() ? next : null;
  };

  if (loading) {
    return (
      <Card className="bg-white border-slate-200" data-testid="introductees-card-loading">
        <CardContent className="p-5">
          <div className="h-4 w-40 bg-slate-100 rounded animate-pulse mb-3" />
          <div className="space-y-2">
            {[0, 1, 2].map((i) => <div key={i} className="h-12 bg-slate-50 rounded animate-pulse" />)}
          </div>
        </CardContent>
      </Card>
    );
  }
  if (!rows) return null;

  const approvedCount = rows.filter((r) => r.stage === "accredited_kyc_approved").length;

  return (
    <Card className="bg-white border-slate-200" data-testid="introductees-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShieldCheck size={14} className="text-blue-600" />
            <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-semibold">
              My Introductees
            </p>
          </div>
          <Badge className="bg-slate-100 text-slate-700 border-slate-200 border text-[10px]" data-testid="introductees-count">
            <Users size={10} className="mr-1" />
            {rows.length} total · {approvedCount} fully approved
          </Badge>
        </div>

        {rows.length === 0 ? (
          <div className="p-6 rounded-md border border-dashed border-slate-200 bg-slate-50/40 text-center" data-testid="introductees-empty">
            <Users size={20} className="text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-700 font-medium">No introductees yet.</p>
            <p className="text-xs text-slate-500 mt-1">
              Share your VC# or invite link from the panel above. As people join with your VC#, they'll
              appear here so you can see exactly where each one is in onboarding.
            </p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="introductees-list">
            {rows.map((r) => {
              const stageMeta = STAGE_LABELS[r.stage] || STAGE_LABELS.registered;
              const pct = STAGE_PROGRESS[r.stage] || 20;
              const Icon = stageMeta.icon;
              const isFullyApproved = r.stage === "accredited_kyc_approved";
              const cooldownUntil = nudgeCooldownUntil(r.last_nudge_at);
              const cooldownLabel = cooldownUntil ? cooldownUntil.toISOString().slice(0, 10) : null;
              return (
                <li
                  key={r.id}
                  className="rounded-md border border-slate-200 p-3 hover:border-slate-300 transition-colors"
                  data-testid={`introductee-row-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{r.full_name || "—"}</p>
                      <div className="flex items-center gap-3 flex-wrap mt-0.5">
                        <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                          <Mail size={9} /> {r.email}
                        </span>
                        {r.phone && (
                          <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                            <Phone size={9} /> {r.phone}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={`${stageMeta.cls} border text-[10px]`} data-testid={`introductee-stage-${r.id}`}>
                        <Icon size={9} className="mr-1" />
                        {stageMeta.label}
                      </Badge>
                      {!isFullyApproved && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!!cooldownUntil || nudgingId === r.id}
                          onClick={() => nudge(r)}
                          className="h-7 px-2 text-[11px] border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-60"
                          data-testid={`nudge-btn-${r.id}`}
                          title={cooldownUntil ? `Next nudge eligible after ${cooldownLabel}` : "Send a compliance-safe reminder email"}
                        >
                          <Send size={10} className="mr-1" />
                          {nudgingId === r.id ? "Sending…" : cooldownUntil ? `Wait · ${cooldownLabel}` : "Nudge"}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        r.stage === "accredited_kyc_approved"
                          ? "bg-gradient-to-r from-emerald-500 to-emerald-400"
                          : "bg-gradient-to-r from-blue-500 to-blue-400"
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <p className="text-[10px] text-slate-400 font-mono">
                      Joined {r.joined_at?.slice(0, 10) || "—"}
                    </p>
                    {r.last_nudge_at && (
                      <p className="text-[10px] text-slate-400 font-mono" data-testid={`last-nudge-${r.id}`}>
                        Last nudge {r.last_nudge_at.slice(0, 10)}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        <p className="text-[11px] text-slate-500 leading-relaxed mt-4">
          You and the new member are both kept in the loop automatically as each onboarding stage
          completes. If someone stalls, use the <strong>Nudge</strong> button — it sends a single
          compliance-safe reminder email (one per introductee per 7 days). The nudge never mentions
          returns, terms, or strategy, only the next verification step.
        </p>
      </CardContent>
    </Card>
  );
};

export default MyIntroducteesCard;
